import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { UserProvider } from "./context/UserContext";
import { TaskProvider } from "./context/TaskContext";
import { ModuleProvider } from "./context/ModuleContext";
import { AdminProvider } from "./context/AdminContext";
import { ConnectProvider } from "./context/ConnectContext";
import { SignUpProvider } from "./context/SignUpContext";

// HOCs for route protection
import PrivateRoute from "./routes/PrivateRoute";
import PublicRoute from "./routes/PublicRoute";
import HomeRedirect from "./routes/HomeRedirect";
import GlobalLoader from "./pages/GlobalLoader";
import { AccountProvider } from "./context/AccountContext";
import { TeamProvider } from "./context/TeamContext";
import { CategoryProvider } from "./context/CategoryContext";
import { TagProvider } from "./context/TagContext";
import FlaggedItemsMain from "./components/Dashboard/Flagged/FlaggedItemsMain";
import { DashboardProvider } from "./context/DashboardContext";
import TaskPropertiesHome from "./components/TaskManagement/TaskNewDesign/TaskPropertiesHome";
import DailySalesHome from "./components/DailySales/DailySalesHome";

const RoomCategoryHome = lazy(() => import("./components/Admin/RoomCategory/RoomCategoryHome"));
const ShareTeamTagsHome = lazy(() => import("./components/Admin/SharedTeamTags/ShareTeamTagsHome"));
const MergeCompanyHome = lazy(() => import("./components/Admin/MergeCompany/MergeCompanyHome"));
const RollbackCompanyHome = lazy(() => import("./components/Admin/RollbackCompany/RollbackCompanyHome"));
const CreateAndEditReport = lazy(() => import("./components/DailyReport/CreateAndEditReport"));
const MuteNotifications = lazy(() => import("./pages/MuteNotifications"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));

const Reply = lazy(() => import("./components/connect/chat/Reply"));
const SharedFileUrl = lazy(() => import("./pages/SharedFileUrl"));
const FileHubUrl = lazy(() => import("./pages/FileHubUrl"));
const AccountSwitch = lazy(() => import("./components/connect/AccountSwitch"));
const Notifications = lazy(() => import("./components/Notifications"));

// Lazy-loaded route components (code-splitting)
const Login = lazy(() => import("./components/Login"));
const NotFound = lazy(() => import("./components/NotFound"));
const Connect = lazy(() => import("./pages/Connect"));
const ChatScreenContainer = lazy(() => import("./components/connect/chat/ChatScreenContainer"));
const AdminPanel = lazy(() => import("./components/Admin/AdminPanel"));
const DailyReportPage = lazy(() => import("./pages/DailyReportPage"));
const SignupHome = lazy(() => import("./components/Signup/SignupHome"));
const CallingMeeting = lazy(() => import("./components/Call_window/Calling_meeting"));
const FileHubHome = lazy(() => import("./components/FileHub/FileHubHome"));
const TasksDashboardPage = lazy(() => import("./pages/TasksDashboardPage"));
const TaskProperties = lazy(() => import("./components/TaskManagement/TaskDetails/TaskProperties"));
const ModuleManageHome = lazy(() => import("./components/Admin/ModuleManagement/ModuleManageHome"));
const TaskSettingsHome = lazy(() => import("./components/Admin/TaskSettings/TaskSettingsHome"));
const CompanyManagementHome = lazy(() => import("./components/Admin/CompanyManagemnet/CompanyManagementHome"));
const UserManagementHome = lazy(() => import("./components/Admin/UserManagement/UserManagementHome"));
const TeamManagementHome = lazy(() => import("./components/Admin/Teammanagement/TeamManagementHome"));
const RoleManagementHome = lazy(() => import("./components/Admin/RoleManagement/RoleManagementHome"));
const VenueManagementHome = lazy(() => import("./components/Admin/VenueManagement/VenueManagementHome"));
const OtpManagementHome = lazy(() => import("./components/Admin/OtpManagement/OtpManagementHome"));
const TaskCreateAndEdit = lazy(() => import("./components/TaskManagement/CreateEidtTask/TaskCreateAndEdit"));
const SubscriptionsBillingCon = lazy(() => import("./components/Admin/SubscriptionsBilling/SubscriptionsBillingCon"));
const BusinessAccountInfo = lazy(() => import("./components/Admin/BusinessAccount/BusinessAccountInfo"));

export const router = createBrowserRouter([
    {
        path: '/',
        element: <HomeRedirect />
    },
    {
        path: "login",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={''} />
                </Suspense>
            </PublicRoute>
        ),
    },
    {
        path: "signin_with_otp",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'signin_with_otp'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/signin_with_otp/:short_url",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'signin_with_otp'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "forgot-password",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <Login type={'forgot-password'} />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "signup",
        element: (
            <PublicRoute>
                <SignUpProvider>
                    <Suspense fallback={<GlobalLoader />}>
                        <SignupHome type="normal" />
                    </Suspense>
                </SignUpProvider>
            </PublicRoute>
        ),
    },
    {
        path: "/signup/:short_url",
        element: (
            <PublicRoute>
                <SignUpProvider>
                    <Suspense fallback={<GlobalLoader />}>
                        <SignupHome type="invitedUser" />
                    </Suspense>
                </SignUpProvider>
            </PublicRoute>
        ),
    },
    {
        path: "/connect",
        element: <PrivateRoute />, // single PrivateRoute
        children: [
            {
                index: true,
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <Connect />
                        </Suspense>
                    </ConnectProvider>
                ),
            },
            {
                path: ":slug",
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <ChatScreenContainer />
                        </Suspense>
                    </ConnectProvider>
                ),
                children: [
                    {
                        path: "file-hub",
                        children: [
                            {
                                path: ":tab", // e.g. tag, files, links
                                element: (
                                    <ConnectProvider>
                                        <Suspense fallback={<GlobalLoader />}>
                                            <FileHubHome />
                                        </Suspense>
                                    </ConnectProvider>
                                ),
                            },
                            {
                                index: true,
                                element: (
                                    <ConnectProvider>
                                        <Suspense fallback={<GlobalLoader />}>
                                            <FileHubHome />
                                        </Suspense>
                                    </ConnectProvider>
                                ),
                            }
                        ]
                    },
                    {
                        path: "reply/:msg_id", // ✅ make this relative
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <Reply />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    },
                    {
                        path: "filter/:filter_name",
                        element: <></>,
                        children: [
                            {
                                path: ":search_string",
                                element: <></>,
                            }
                        ]
                    },
                    {
                        path: "tasks",
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <TasksDashboardPage />
                                </Suspense>
                            </ConnectProvider>
                        ),
                        children: [
                            { path: "create-task", element: (<Suspense fallback={<GlobalLoader />}><TaskCreateAndEdit /></Suspense>) },
                            { path: "edit-task", element: (<Suspense fallback={<GlobalLoader />}><TaskCreateAndEdit /></Suspense>) },
                            {
                                path: ":task_id",
                                element: (
                                    <TaskProvider>
                                        <Suspense fallback={<GlobalLoader />}>
                                            <TaskPropertiesHome />
                                        </Suspense>
                                    </TaskProvider>
                                ),
                            }
                        ]
                    },
                    {
                        path: ":task_id",
                        element: (
                            <TaskProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <TaskPropertiesHome />
                                </Suspense>
                            </TaskProvider>
                        ),
                    },
                ]
            },
            {
                path: "profile",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <UserProfilePage />
                    </Suspense>
                ),
            },
            {
                path: "daily-sales",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <DailySalesHome />
                    </Suspense>
                ),
            },
            {
                path: "notifications",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <Notifications />
                    </Suspense>
                ),
            },
            {
                path: "mute-notifications",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <MuteNotifications />
                    </Suspense>
                )
            },
            {
                path: "switch-account",
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <AccountSwitch />
                        </Suspense>
                    </ConnectProvider>
                ),
            },
            // {
            //     path: "flagged-items",
            //     element: (
            //         <ConnectProvider>
            //             <Suspense fallback={<GlobalLoader />}>
            //                 <DashboardProvider>
            //                     <FlaggedItemsMain />
            //                 </DashboardProvider>
            //             </Suspense>
            //         </ConnectProvider>
            //     ),
            // },
            {
                path: "file-hub",
                children: [
                    {
                        path: ":tab", // e.g. tag, files, links
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <FileHubHome />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    },
                    {
                        index: true,
                        element: (
                            <ConnectProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <FileHubHome />
                                </Suspense>
                            </ConnectProvider>
                        ),
                    }
                ]
            },
            // ...existing code...
            {
                path: "tasks",
                element: (
                    <ConnectProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <TasksDashboardPage />
                        </Suspense>
                    </ConnectProvider>
                ),
                children: [
                    { path: "create-task", element: <TaskCreateAndEdit /> },
                    { path: "edit-task", element: <TaskCreateAndEdit /> },
                    {
                        path: ":task_id",
                        element: (
                            <TaskProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <TaskPropertiesHome />
                                </Suspense>
                            </TaskProvider>
                        ),
                    },
                    {
                        path: ":task_id/old",
                        element: (
                            <TaskProvider>
                                <Suspense fallback={<GlobalLoader />}>
                                    <TaskProperties/>
                                </Suspense>
                            </TaskProvider>
                        ),
                    }
                ]
            },
            {
                path: "daily-report",
                element: (
                    <Suspense fallback={<GlobalLoader />}>
                        <DailyReportPage />
                    </Suspense>
                ),
                children: [
                    { path: "create-reports", element: <Suspense fallback={<GlobalLoader />}><CreateAndEditReport /></Suspense> },
                    { path: "edit-report", element: <Suspense fallback={<GlobalLoader />}><CreateAndEditReport /></Suspense> }
                ]
            },
            {
                path: "adminpanel",
                element: (
                    <AdminProvider>
                        <Suspense fallback={<GlobalLoader />}>
                            <AdminPanel />
                        </Suspense>
                    </AdminProvider>
                ),
                children: [
                    { path: "subscription-management", element: <Suspense fallback={<GlobalLoader />}><SubscriptionsBillingCon /></Suspense> },
                    { path: "user-management", element: <UserProvider> <Suspense fallback={<GlobalLoader />}><UserManagementHome /> </Suspense></UserProvider> },
                    { path: "account-information", element: <AccountProvider> <Suspense fallback={<GlobalLoader />}><BusinessAccountInfo /></Suspense> </AccountProvider> },
                    { path: "otp-management", element: <Suspense fallback={<GlobalLoader />}><OtpManagementHome /></Suspense> },
                    { path: "team-management", element: <TeamProvider>  <Suspense fallback={<GlobalLoader />}><TeamManagementHome /> </Suspense> </TeamProvider> },
                    { path: "room-category", element: <CategoryProvider>  <Suspense fallback={<GlobalLoader />}><RoomCategoryHome /> </Suspense> </CategoryProvider> },
                    { path: "module-management", element: <ModuleProvider><Suspense fallback={<GlobalLoader />}><ModuleManageHome /></Suspense></ModuleProvider> },
                    { path: "task-settings", element: <ModuleProvider><Suspense fallback={<GlobalLoader />}><TaskSettingsHome /></Suspense></ModuleProvider> },
                    { path: "role-management", element: <Suspense fallback={<GlobalLoader />}><RoleManagementHome /></Suspense> },
                    { path: "shared-tags", element: <TagProvider> <Suspense fallback={<GlobalLoader />}><ShareTeamTagsHome /></Suspense></TagProvider> },
                    { path: "merge-company", element: <Suspense fallback={<GlobalLoader />}><MergeCompanyHome /></Suspense> },
                    { path: "rollback-company", element: <Suspense fallback={<GlobalLoader />}><RollbackCompanyHome /></Suspense> },
                    { path: "venue-management", element: <Suspense fallback={<GlobalLoader />}><VenueManagementHome /></Suspense> },
                    { path: "company-management", element: <Suspense fallback={<GlobalLoader />}><CompanyManagementHome /></Suspense> }
                ]
            }
        ]
    },
    {
        path: "/f/:url",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <SharedFileUrl />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/file",
        element: (
            <PublicRoute>
                <Suspense fallback={<GlobalLoader />}>
                    <FileHubUrl />
                </Suspense>
            </PublicRoute>
        )
    },
    {
        path: "/call/:cid",
        element: (
            <Suspense fallback={<GlobalLoader />}>
                <CallingMeeting />
            </Suspense>
        ),
    },
    {
        path: "/c/:id",
        element: (
            <Suspense fallback={<GlobalLoader />}>
                <CallingMeeting />
            </Suspense>
        ),
    },
    {
        path: "/u/:id",
        element: (
            <Suspense fallback={<GlobalLoader />}>
                <CallingMeeting />
            </Suspense>
        ),
    },
    {
        path: "*", // This wildcard route will match any undefined paths
        element: (
            <Suspense fallback={<GlobalLoader />}>
                <NotFound />
            </Suspense>
        ), // Render the NotFound component for unmatched routes
    },
])